import { Link, useSearchParams } from 'react-router-dom'
import { Inbox, Check, AlertTriangle, X, Pencil } from 'lucide-react'
import {
  useBenchmarksPending,
  useAdminBenchmarksPendingCount,
  usePublishBenchmark,
  useSuppressBenchmark,
  type Benchmark,
  type BenchmarkArchiveStatus,
  type PublisherTypeFilter,
} from '@/hooks/useBenchmarkQueries'
import { ErrorState } from '@/components/shared/ErrorState'
import { EmptyState } from '@/components/shared/EmptyState'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { RouteToVendorAction } from '@/components/vendor-content/RouteToVendorAction'
// KAN-822 T9 — the Route to Vendor action is DARK (one shared build-flag reader).
import { vendorRoutingUiEnabled } from '@/components/vendor-content/routing-ui-flag'

/**
 * SP2 + SP4 Wave 1 — admin Pending Review queue.
 *
 * Filters `benchmarks` on `status='draft' AND createdByEmail LIKE 'system:%'`
 * so it lists only rows inserted by the automation (benchmark-rss from SP2
 * or benchmark-analyst from SP3 Wave 2). Admin can inline-`[Publish]`,
 * inline-`[Suppress]`, or open the existing SP1 `BenchmarkEditPage` for a
 * full review pass.
 *
 * The route is rendered only when `VITE_BENCHMARKS_SP1_ENABLED=true`; the
 * flag gate lives in `App.tsx` so the component itself doesn't need to
 * recheck it.
 *
 * SP3 Wave 2 adds a publisher-type filter chip row (All / Vendor / Analyst /
 * …) above the table and a `Source` column with a coloured pill on each
 * row. The active chip is persisted in the URL query string
 * (`?publisherType=analyst`) so deep-linking + back-button navigation work.
 */

const CHIP_DEFS: ReadonlyArray<{
  key: PublisherTypeFilter | 'all'
  label: string
}> = [
  { key: 'all', label: 'All' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'analyst', label: 'Analyst' },
  { key: 'research_firm', label: 'Research firm' },
  { key: 'government_agency', label: 'Government' },
  { key: 'consortium', label: 'Consortium' },
  { key: 'other', label: 'Other' },
]

export function BenchmarksPendingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const rawFilter = searchParams.get('publisherType') ?? 'all'
  const activeFilter: PublisherTypeFilter | 'all' =
    (CHIP_DEFS.map((c) => c.key) as string[]).includes(rawFilter)
      ? (rawFilter as PublisherTypeFilter | 'all')
      : 'all'

  const publisherTypeParam =
    activeFilter === 'all' ? undefined : activeFilter

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useBenchmarksPending(50, 0, publisherTypeParam)
  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <main className="flex flex-col gap-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Inbox className="h-5 w-5 text-primary" aria-hidden />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Pending Review ({total})</h1>
            <p className="text-sm text-muted-foreground">
              Auto-discovered from vendor feeds + analyst press releases.
              Review, publish, or suppress.
            </p>
          </div>
        </div>
      </header>

      <PublisherTypeChipRow
        active={activeFilter}
        onChange={(next) => {
          const sp = new URLSearchParams(searchParams)
          if (next === 'all') sp.delete('publisherType')
          else sp.set('publisherType', next)
          setSearchParams(sp, { replace: true })
        }}
      />

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState
          title="Failed to load pending benchmarks"
          message="We couldn't reach the admin benchmarks API. Try again in a moment."
          onRetry={() => refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No pending benchmarks"
          description={
            activeFilter === 'all'
              ? 'The auto-discovery adapters have no unreviewed items right now. Check back after the next scheduler tick.'
              : `No unreviewed items match the "${activeFilter.replace('_', ' ')}" filter. Try a different source type.`
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3 font-semibold">Title</th>
                <th className="p-3 font-semibold">Publisher</th>
                <th className="p-3 font-semibold">Source</th>
                <th className="p-3 font-semibold">Draft summary</th>
                <th className="p-3 font-semibold">PDF</th>
                <th className="p-3 font-semibold">Age</th>
                <th className="p-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <PendingRow key={b.id} item={b} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

function PublisherTypeChipRow({
  active,
  onChange,
}: {
  active: PublisherTypeFilter | 'all'
  onChange: (next: PublisherTypeFilter | 'all') => void
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="toolbar"
      aria-label="Filter pending benchmarks by source type"
      data-testid="publisher-type-chip-row"
    >
      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        Source type:
      </span>
      {CHIP_DEFS.map((chip) => (
        <PublisherTypeChip
          key={chip.key}
          chip={chip}
          active={active === chip.key}
          onClick={() => onChange(chip.key)}
        />
      ))}
    </div>
  )
}

function PublisherTypeChip({
  chip,
  active,
  onClick,
}: {
  chip: { key: PublisherTypeFilter | 'all'; label: string }
  active: boolean
  onClick: () => void
}) {
  const publisherType =
    chip.key === 'all' ? undefined : (chip.key as PublisherTypeFilter)
  const { data } = useAdminBenchmarksPendingCount(publisherType)
  const countLabel = typeof data?.count === 'number' ? ` (${data.count})` : ''
  return (
    <Button
      size="sm"
      variant={active ? 'default' : 'outline'}
      aria-pressed={active}
      onClick={onClick}
      data-chip={chip.key}
      data-testid={`chip-${chip.key}`}
    >
      {chip.label}
      {countLabel}
    </Button>
  )
}

function PendingRow({ item }: { item: Benchmark }) {
  const publish = usePublishBenchmark(item.id)
  const suppress = useSuppressBenchmark(item.id)
  const draftPreview = (item.draftSummary ?? '').slice(0, 120)
  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/20">
      <td className="p-3 font-medium">
        <Link to={`/admin/benchmarks/${item.id}/edit`} className="hover:underline">
          {item.title}
        </Link>
      </td>
      <td className="p-3 text-muted-foreground">{item.publisher}</td>
      <td className="p-3">
        <PublisherTypeBadge type={item.publisherType} />
      </td>
      <td className="p-3 text-muted-foreground">
        {draftPreview ? (
          <span title={item.draftSummary ?? undefined}>{draftPreview}{draftPreview.length === 120 ? '…' : ''}</span>
        ) : (
          <span className="italic text-muted-foreground/60">no summary</span>
        )}
      </td>
      <td className="p-3">
        <PdfStatusCell status={item.archiveStatus} />
      </td>
      <td className="p-3 text-muted-foreground">{formatDate(item.publishedAt)}</td>
      <td className="p-3 text-right">
        <div className="inline-flex items-center gap-1">
          <Button asChild variant="ghost" size="sm">
            <Link
              to={`/admin/benchmarks/${item.id}/edit`}
              className="inline-flex items-center gap-1"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Review
            </Link>
          </Button>
          <Button
            size="sm"
            variant="default"
            disabled={publish.isPending || publish.isSuccess}
            onClick={() => publish.mutate()}
          >
            {publish.isPending ? 'Publishing…' : publish.isSuccess ? 'Published' : 'Publish'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={suppress.isPending || suppress.isSuccess}
            onClick={() => suppress.mutate({})}
          >
            {suppress.isPending ? 'Suppressing…' : suppress.isSuccess ? 'Suppressed' : 'Suppress'}
          </Button>
          {vendorRoutingUiEnabled() && <RouteToVendorAction benchmark={item} />}
        </div>
      </td>
    </tr>
  )
}

const PUBLISHER_TYPE_BADGE: Record<string, { label: string; classes: string }> = {
  vendor: {
    label: 'Vendor',
    classes:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  },
  analyst: {
    label: 'Analyst',
    classes:
      'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  },
  research_firm: {
    label: 'Research firm',
    classes:
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
  },
  government_agency: {
    label: 'Government',
    classes:
      'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300',
  },
  consortium: {
    label: 'Consortium',
    classes:
      'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  },
  other: {
    label: 'Other',
    classes:
      'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
}

function PublisherTypeBadge({ type }: { type: string }) {
  const cfg = PUBLISHER_TYPE_BADGE[type] ?? PUBLISHER_TYPE_BADGE.other
  return (
    <span
      data-testid={`publisher-type-${type}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.classes}`}
    >
      {cfg.label}
    </span>
  )
}

function PdfStatusCell({ status }: { status: BenchmarkArchiveStatus | undefined }) {
  if (status === 'archived') {
    return (
      <span
        data-testid="pdf-status-archived"
        className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
      >
        <Check className="h-3 w-3" aria-hidden /> Archived
      </span>
    )
  }
  if (status === 'link_only') {
    return (
      <span
        data-testid="pdf-status-link_only"
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
      >
        <AlertTriangle className="h-3 w-3" aria-hidden /> Link only
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span
        data-testid="pdf-status-failed"
        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-950 dark:text-red-300"
      >
        <X className="h-3 w-3" aria-hidden /> Failed
      </span>
    )
  }
  return (
    <span
      data-testid="pdf-status-pending"
      className="text-xs text-muted-foreground"
    >
      pending
    </span>
  )
}
